/**
 * Auth0 invitation landing params (D15-14). An invitation link opens
 * `/login?invitation=<ticket>&organization=org_…&organization_name=…`.
 *
 * Both `invitation` and `organization` must be present and well-formed, or the
 * whole thing is ignored and the default login runs. The ticket is a
 * credential-like value: it is only ever handed to the one authorize call —
 * never stored, logged, or used to derive the tenant (the token's `org_id` is
 * the only tenant source, ADR-0033).
 */
export interface Invitation {
  invitation: string;
  organization: string;
  /** Display only; undefined when absent or malformed. */
  organizationName?: string;
}

const TICKET = /^[A-Za-z0-9_-]{1,128}$/;
const ORG_ID = /^org_[A-Za-z0-9]{1,64}$/;
const ORG_NAME = /^[A-Za-z0-9_-]{1,64}$/;

export const INVITATION_PARAMS = ['invitation', 'organization', 'organization_name'] as const;

export function parseInvitation(params: URLSearchParams): Invitation | undefined {
  const invitation = params.get('invitation');
  const organization = params.get('organization');
  if (!invitation || !organization) return undefined;
  if (!TICKET.test(invitation) || !ORG_ID.test(organization)) return undefined;
  const name = params.get('organization_name');
  return {
    invitation,
    organization,
    organizationName: name && ORG_NAME.test(name) ? name : undefined,
  };
}

/** Drop the three params from the address bar so the ticket doesn't linger in history. */
export function stripInvitationParams(): void {
  const url = new URL(window.location.href);
  for (const key of INVITATION_PARAMS) url.searchParams.delete(key);
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
}
